/**
 * Painel — o container mais usado das telas de produção.
 *
 * Recolher é estado LOCAL, não do documento. `props.collapsed` diz como o painel
 * nasce; o que o usuário faz depois não volta para o `ScreenDocument`. Sem essa
 * separação, abrir um painel numa tela publicada marcaria a tela como alterada,
 * e o guard de saída dispararia sem ninguém ter editado nada.
 */
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { ScreenNode } from '../../model/node-props';
import { trackByNodeId } from '../../model/screen-index';
import { TranslateTAPipe } from '../translate-ta.pipe';
import { NodeHostComponent } from '../node-host.component';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { childLayoutStyles, safeRawStyles, styleTokenClasses } from './child-layout';

@Component({
  selector: 'tem-panel-container',
  standalone: true,
  imports: [CommonModule, NzIconModule, NodeHostComponent, TranslateTAPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section
      class="tem-panel"
      [ngClass]="themeClasses()"
      [class.tem-panel--disabled]="isDisabled()"
      [ngStyle]="rawStyles()">

      @if (title().length > 0) {
        <header
          class="tem-panel__header"
          [class.tem-panel__header--clickable]="isCollapsible()"
          [attr.role]="isCollapsible() ? 'button' : null"
          [attr.tabindex]="isCollapsible() ? 0 : null"
          [attr.aria-expanded]="isCollapsible() ? !collapsed() : null"
          (click)="toggle()"
          (keydown.enter)="toggle()"
          (keydown.space)="toggle()">

          <span>{{ title() | translateTA }}</span>

          @if (isCollapsible()) {
            <nz-icon
              class="tem-panel__toggle"
              [nzType]="collapsed() ? 'right' : 'down'" />
          }
        </header>
      }

      @if (!collapsed()) {
        <div class="tem-panel__body">
          @for (child of children(); track trackBy(0, child)) {
            <div [ngStyle]="layoutOf(child)">
              <tem-node-host [node]="child" [form]="form()" [mode]="mode()" />
            </div>
          }
        </div>
      }
    </section>
  `,
  styleUrl: './panel.renderer.scss'
})
export class PanelContainerRenderer implements NodeRenderer<'panel'> {

  readonly node = input.required<ScreenNode<'panel'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly trackBy = trackByNodeId;

  /** Estado local do recolhimento. Nasce do documento, e ali para. */
  private readonly userCollapsed = signal<boolean | null>(null);

  readonly children = computed<readonly ScreenNode[]>(() => this.node().children ?? []);
  readonly title = computed<string>(() => this.node().props.nameKey ?? '');
  readonly isCollapsible = computed<boolean>(() => this.node().props.collapsible === true);
  readonly isDisabled = computed<boolean>(() => this.node().props.disabled === true);

  readonly collapsed = computed<boolean>(() => {
    const chosen = this.userCollapsed();
    if (chosen !== null) {
      return chosen;
    }
    return this.node().props.collapsed === true;
  });

  readonly themeClasses = computed(() => styleTokenClasses(this.node().style));
  readonly rawStyles = computed(() => safeRawStyles(this.node().style));

  toggle(): void {
    if (!this.isCollapsible()) {
      return;
    }
    this.userCollapsed.set(!this.collapsed());
  }

  layoutOf(child: ScreenNode): Readonly<Record<string, string>> {
    return childLayoutStyles(child);
  }
}
